import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllCompanies, searchCompanies } from '../services/companyService';
import Footer from '../components/Footer';

const CompaniesList = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCompanies, setFilteredCompanies] = useState([]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const result = await getAllCompanies(50);

        if (result.success) {
          setCompanies(result.data);
          setFilteredCompanies(result.data);
        } else {
          setError(result.error);
        }
      } catch (error) {
        setError('Failed to load companies');
        console.error('Error fetching companies:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    const handleSearch = async () => {
      if (searchTerm.trim() === '') {
        setFilteredCompanies(companies);
        return;
      }

      try {
        const result = await searchCompanies(searchTerm);
        if (result.success) {
          setFilteredCompanies(result.data);
        } else {
          // Fallback to local filtering
          const filtered = companies.filter(company =>
            company.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            company.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            company.location?.toLowerCase().includes(searchTerm.toLowerCase())
          );
          setFilteredCompanies(filtered);
        }
      } catch (error) {
        // Fallback to local filtering
        const filtered = companies.filter(company =>
          company.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          company.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          company.location?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredCompanies(filtered);
      }
    };

    const timeoutId = setTimeout(handleSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, companies]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Companies</h1>
          <p className="mt-2 text-gray-600">Discover companies and learn about their culture, values, and opportunities.</p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Companies Grid */}
        {filteredCompanies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompanies.map((company) => (
              <Link
                key={company.id}
                to={`/companies/${company.id}`}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200 overflow-hidden"
              >
                {/* Company Header */}
                <div className="h-32 bg-gradient-to-r from-blue-600 to-purple-600 relative">
                  {company.companyImages && company.companyImages.length > 0 && (
                    <img
                      src={company.companyImages[0]}
                      alt="Company cover"
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-40"></div>

                  {/* Company Logo */}
                  <div className="absolute bottom-0 left-4 transform translate-y-1/2">
                    <div className="bg-white p-1 rounded-lg shadow-lg">
                      {company.logoUrl ? (
                        <img
                          src={company.logoUrl}
                          alt={`${company.companyName} logo`}
                          className="h-16 w-16 object-contain"
                        />
                      ) : (
                        <div className="h-16 w-16 bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-xl font-bold text-gray-400">
                            {company.companyName?.charAt(0)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Company Info */}
                <div className="p-6 pt-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{company.companyName}</h3>
                  <p className="text-gray-600 mb-2">{company.industry}</p>

                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    {company.location}
                  </div>

                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                    </svg>
                    {company.companySize}
                  </div>

                  {company.description && (
                    <p className="text-gray-700 text-sm line-clamp-3 mb-4">
                      {company.description}
                    </p>
                  )}

                  {/* Profile Completion */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${company.profileCompletion || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-500">{company.profileCompletion || 0}%</span>
                    </div>

                    <span className="text-blue-600 text-sm font-medium">View Profile →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No companies found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search terms.' : 'No companies have been registered yet.'}
            </p>
          </div>
        )}

        {/* Load More Button */}
        {filteredCompanies.length > 0 && (
          <div className="mt-8 text-center">
            <button className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700">
              Load More Companies
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default CompaniesList;
